import { type MaybeRef, computed, unref } from "vue";
import { useI18n } from "vue-i18n";

import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/vue-query";

import { ONE_MINUTE_MS } from "@/helpers/constants";
import { useNotification } from "@/hooks/notifications";
import { COMMUNICATION_LOGS_KEY } from "@/hooks/queries/useInbox";
import communicationService from "@/services/modules/communication";
import { useCommunicationStore } from "@/stores/communication";

import type { IPaginatedResponse } from "@/models/common";
import type { CallLogData, CallNotePayload } from "@/models/communicationLogs";
import type { SmsLogData } from "@/models/communicationLogs";

const QUERY_KEYS = {
  updateNote: (id: number) => ["update-call-note", id] as const,
  getSmsConversation: (id: number) => ["get-sms-conversation", id] as const,
  replyToSms: (id: number) => ["reply-to-sms", id] as const,
} as const;

export const useUpdateCallNoteMutation = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();
  const { updateCallNote } = useCommunicationStore();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: CallLogData["id"];
      payload: CallNotePayload;
    }): Promise<void> => updateCallNote(id, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.updateNote(variables.id),
      });
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === COMMUNICATION_LOGS_KEY,
      });
    },
    onError: () => {
      showMessage(t("ERRORS.NOTE_UPDATE_ERROR"), "error");
    },
  });
};

export const useSmsConversationQuery = (id: MaybeRef<number>) => {
  const queryKey = computed(() => [
    ...QUERY_KEYS.getSmsConversation(unref(id)),
  ]);
  return useInfiniteQuery<
    IPaginatedResponse<SmsLogData>,
    Error,
    InfiniteData<IPaginatedResponse<SmsLogData>>,
    (string | number)[],
    number
  >({
    initialPageParam: 1,
    queryKey,
    queryFn: ({ pageParam }) =>
      communicationService.getSmsConversation(unref(id), {
        sort_by: "-created_at",
        page: (pageParam as number | undefined) ?? 1,
      }),
    enabled: computed(() => !!unref(id)),
    staleTime: 2 * ONE_MINUTE_MS,
    getNextPageParam: (lastPage) => {
      if (!lastPage.links.next) {
        return undefined;
      }
      const url = new URL(lastPage.links.next);
      const nextPage = url.searchParams.get("page");
      return nextPage ? Number(nextPage) : undefined;
    },
    getPreviousPageParam: () => undefined,
  });
};

export const useReplyToSmsMutation = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useNotification();
  const { t } = useI18n();
  return useMutation({
    mutationFn: ({
      id,
      message,
      from,
    }: {
      id: number;
      message: string;
      from: string;
    }): Promise<{ data: SmsLogData }> => {
      return communicationService.replyToSms(id, message, from);
    },
    onSuccess: (data, variables) => {
      showMessage(t("ACTIVITY.SMS.SUCCESS_MESSAGE"), "success");
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.replyToSms(variables.id),
      });
      if (data?.data?.sms_conversation_id) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.getSmsConversation(
            data.data.sms_conversation_id
          ),
        });
      }
    },
    onError: () => {
      showMessage(t("COMMON.ERROR_OCCURRED"), "error");
    },
  });
};
